'use client'

import { reportHandledException } from '@/lib/error-reporting'
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Field, Input, SegmentedControl, toast } from '@kiao-voice/ui-kit'
import { readStoredToken } from '../auth/auth-storage'
import { fetchPersonalInvoiceSettings, type InvoiceSettings } from '../billing/api-client'
import { CheckoutDialog } from '../billing/checkout/checkout-dialog'
import { createTeamSubscribeStrategy } from '../billing/checkout/checkout-strategies'
import type { CheckoutStrategy, CheckoutSuccessOutcome } from '../billing/checkout/checkout-types'
import { yearlyDiscountPct } from '../billing/plan-pricing'
import { subscribeTeam, type TeamPlanPricing } from './api-client'
import { writeTeamSubscriptionThreeDsIntent } from './team-three-ds-intent'
import type { SubscriptionCycle } from '@kiao-voice/core'

interface TeamSubscribeModalProps {
  open: boolean
  pricing: TeamPlanPricing
  userEmail: string | null
  /** Defaults to yearly — the cheaper per-month price and the cycle we steer buyers to. */
  initialCycle?: SubscriptionCycle
  initialSeats?: number
  initialTeamName?: string
  resetKey: string
  onClose: () => void
  onSuccess?: () => void
  /** Test seams. */
  getTokenFn?: () => string | null
  subscribeFn?: typeof subscribeTeam
  redirect?: (url: string) => void
  skipScript?: boolean
  invoiceLoadFn?: typeof fetchPersonalInvoiceSettings
  persistThreeDsIntentFn?: typeof writeTeamSubscriptionThreeDsIntent
}

const MAX_TEAM_SEATS = 500
const formatAmount = (value: number) => value.toLocaleString('en-US')

function authoritativeTeamTerms(
  value: unknown
): { seats: number; cycle: SubscriptionCycle } | null {
  if (!value || typeof value !== 'object') return null
  const candidate = value as { seats?: unknown; cycle?: unknown }
  if (!Number.isInteger(candidate.seats) || Number(candidate.seats) < 1) return null
  if (candidate.cycle !== 'monthly' && candidate.cycle !== 'yearly') return null
  return { seats: Number(candidate.seats), cycle: candidate.cycle }
}

/** Team checkout composed from the shared purchase shell. */
export function TeamSubscribeModal({
  open,
  pricing,
  userEmail,
  initialCycle = 'yearly',
  initialSeats,
  initialTeamName = '',
  resetKey,
  onClose,
  onSuccess,
  getTokenFn = readStoredToken,
  subscribeFn = subscribeTeam,
  redirect,
  skipScript,
  invoiceLoadFn = fetchPersonalInvoiceSettings,
  persistThreeDsIntentFn = writeTeamSubscriptionThreeDsIntent,
}: TeamSubscribeModalProps) {
  const { t } = useTranslation('web')
  const nameId = useId()
  const seatsId = useId()
  const nameRef = useRef<HTMLInputElement>(null)
  const wasOpenRef = useRef(false)
  const previousResetKey = useRef(resetKey)
  const pendingInvoiceSettingsRef = useRef<InvoiceSettings | null>(null)
  const [openSeq, setOpenSeq] = useState(0)
  const defaultSeats = Math.min(MAX_TEAM_SEATS, Math.max(pricing.minSeats,
    Number.isFinite(initialSeats) ? Math.trunc(initialSeats!) : pricing.minSeats))
  const [seats, setSeats] = useState(defaultSeats)
  const [cycle, setCycle] = useState<SubscriptionCycle>(initialCycle)
  const [teamName, setTeamName] = useState(initialTeamName)
  const [nameError, setNameError] = useState(false)
  const [checkoutSubmitting, setCheckoutSubmitting] = useState(false)

  useEffect(() => {
    if (open && (!wasOpenRef.current || previousResetKey.current !== resetKey)) {
      setSeats(defaultSeats)
      setCycle(initialCycle)
      setTeamName(initialTeamName)
      setNameError(false)
      pendingInvoiceSettingsRef.current = null
      setOpenSeq((value) => value + 1)
    }
    wasOpenRef.current = open
    previousResetKey.current = resetKey
  }, [open, initialCycle, defaultSeats, initialTeamName, resetKey])

  useEffect(() => {
    if (nameError && !checkoutSubmitting) nameRef.current?.focus()
  }, [checkoutSubmitting, nameError])

  const perSeat = cycle === 'yearly' ? pricing.yearlyPriceTwd : pricing.monthlyPriceTwd
  const total = perSeat * seats
  const discountPct = yearlyDiscountPct(pricing.monthlyPriceTwd, pricing.yearlyPriceTwd)
  const formattedPerSeat = perSeat.toLocaleString('en-US')
  const formattedTotal = total.toLocaleString('en-US')

  // Team checkout cannot use the generic settings endpoint here: before the
  // first purchase the active billing context is still personal. Capture the
  // validated form and send it with subscribe so the API persists it against
  // the newly-created Team organization in the same transaction.
  const captureInvoiceSettings = useCallback(async (settings: InvoiceSettings) => {
    pendingInvoiceSettingsRef.current = settings
    return { invoiceSettings: settings }
  }, [])

  const subscribeWithThreeDsIntent = useCallback<typeof subscribeTeam>(
    async (payload, opts) => {
      const result =
        opts === undefined ? await subscribeFn(payload) : await subscribeFn(payload, opts)
      if (result.requires3ds) {
        // The generic return page cannot infer which product sent the buyer.
        // Persist the exact Team trade before CheckoutPanel navigates away, so
        // an already-active personal subscription cannot produce a false-ready
        // result when this Team checkout is still pending.
        try {
          persistThreeDsIntentFn(result.tradeId ?? 0)
        } catch (cause) {
          reportHandledException(cause, { source: 'features/team/team-subscribe-modal.tsx' })
          // Exact correlation is also embedded in the server-authored return
          // URL. Optional browser storage must not block the bank redirect
          // after a pending trade has already been created.
          console.warn('Team checkout: 3DS session intent could not be persisted', cause)
        }
      }
      return result
    },
    [persistThreeDsIntentFn, subscribeFn]
  )

  const strategy = useMemo<CheckoutStrategy>(
    () => ({
      async submit(input) {
        const name = teamName.trim()
        if (!name) {
          setNameError(true)
          return { kind: 'noop' }
        }
        return createTeamSubscribeStrategy({
          seats,
          cycle,
          name,
          amount: total,
          subscribeTeamFn: subscribeWithThreeDsIntent,
          getTokenFn,
          getInvoiceSettings: () => pendingInvoiceSettingsRef.current,
          t,
        }).submit(input)
      },
    }),
    [cycle, getTokenFn, seats, subscribeWithThreeDsIntent, t, teamName, total]
  )

  const updateSeats = (next: number) => {
    if (!Number.isFinite(next)) return
    setSeats(Math.min(MAX_TEAM_SEATS, Math.max(pricing.minSeats, Math.trunc(next))))
  }

  const handleCheckoutSuccess = (outcome: CheckoutSuccessOutcome) => {
    if (outcome.deduplicated) {
      const authoritative = authoritativeTeamTerms(outcome.subscription)
      if (authoritative) {
        setSeats(authoritative.seats)
        setCycle(authoritative.cycle)
      }
      toast.info(t('team.subscriptionAlreadyExists'))
    } else {
      toast.success(t('team.seatsUpdated'))
    }
    onSuccess?.()
  }

  const setup = (
    <>
      <p className="checkout-col-title">{t('team.checkoutDetails')}</p>
      <div className="team-checkout-fields">
        <Field
          label={t('team.createTeamNameLabel')}
          htmlFor={nameId}
          error={nameError ? t('team.nameRequired') : null}
          className="team-checkout-name"
        >
          <Input
            ref={nameRef}
            id={nameId}
            type="text"
            data-testid="team-name-input"
            value={teamName}
            maxLength={120}
            placeholder={t('team.createTeamNamePlaceholder')}
            aria-invalid={nameError}
            onChange={(event) => {
              setTeamName(event.target.value)
              if (nameError) setNameError(false)
            }}
          />
        </Field>

        <Field
          label={<span id={`${nameId}-cycle`}>{t('team.billingCycleLabel')}</span>}
          className="team-checkout-field"
        >
          <SegmentedControl
            aria-labelledby={`${nameId}-cycle`}
            value={cycle}
            options={[
              { value: 'monthly', label: t('team.cycleMonthly') },
              {
                value: 'yearly',
                label: t('team.cycleYearlyDiscount', { pct: discountPct }),
              },
            ]}
            onValueChange={setCycle}
          />
        </Field>

        <Field label={t('team.seatsCountLabel')} htmlFor={seatsId} className="team-checkout-field">
          <div className="stepper">
            <button
              type="button"
              aria-label={t('team.removeSeatAria')}
              disabled={seats <= pricing.minSeats}
              onClick={() => updateSeats(seats - 1)}
            >
              −
            </button>
            <input
              id={seatsId}
              className="stepper-val"
              type="number"
              min={pricing.minSeats}
              max={MAX_TEAM_SEATS}
              step={1}
              value={seats}
              onChange={(event) => updateSeats(Number(event.target.value))}
            />
            <button
              type="button"
              aria-label={t('team.addSeatAria')}
              disabled={seats >= MAX_TEAM_SEATS}
              onClick={() => updateSeats(seats + 1)}
            >
              +
            </button>
          </div>
        </Field>
      </div>
    </>
  )

  return (
    <CheckoutDialog
      visible={open}
      onClose={onClose}
      title={t('team.subscribeHeading')}
      dialogTestId="team-checkout-modal"
      closeTestId="team-checkout-close"
      amount={total}
      wallet={null}
      coupon={{ product: 'team', cycle, seats }}
      userEmail={userEmail}
      strategy={strategy}
      note={t('billing.payNote')}
      setup={setup}
      subtotalLabel={t('team.checkoutSubtotal', { price: formattedPerSeat, seats })}
      summaryNote={
        <p className="checkout-renewal">
          {t('team.renewalNote', {
            period: t(
              cycle === 'yearly' ? 'team.renewalPeriodYearly' : 'team.renewalPeriodMonthly'
            ),
            amount: formattedTotal,
          })}
        </p>
      }
      formatAmount={formatAmount}
      cardFree
      testIdPrefix="team"
      formTestId="team-subscribe-form"
      resetKey={`${resetKey}-${openSeq}`}
      quoteResetKey={`${resetKey}-${openSeq}-${cycle}-${seats}`}
      onSubmittingChange={setCheckoutSubmitting}
      onSuccessOutcome={handleCheckoutSuccess}
      redirect={redirect}
      skipScript={skipScript}
      invoiceLoadFn={invoiceLoadFn}
      invoiceSaveFn={captureInvoiceSettings}
    />
  )
}
